import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import usePagination from '@/hooks/usePagination';
import {
  useCreateNewVersionMutation,
  useConfirmPublishMutation,
  useGetVersionFilesQuery,
} from '@/services/RoutingManagementService';
import { setLatestVersion } from '@/store/publishSlice';
import { RootState } from '@/store/store';
import { waitForPublishConfirmation } from '../utils/publishConfirmation';

import { useSnackbar } from 'notistack';

import { KeenIcon } from '@/components';

interface PublishVersionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPublishSuccess: () => void;
}

const PublishVersionModal: React.FC<PublishVersionModalProps> = ({
  isOpen,
  onClose,
  onPublishSuccess,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [versionName, setVersionName] = useState('');
  const [releaseNote, setReleaseNote] = useState('');
  const [versionNameError, setVersionNameError] = useState<string | null>(null);
  const [releaseNoteError, setReleaseNoteError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [publishProgressMessage, setPublishProgressMessage] =
    useState<string>('Publishing...');
  const latestVersion = useSelector(
    (state: RootState) => state.publish.latestVersion
  );
  const nextVersionNumber = (Number(latestVersion) || 0) + 1;

  const { pagination } = usePagination();
  const dispatch = useDispatch();

  const queryParams = {
    page: pagination.pageIndex + 1,
    page_size: pagination.pageSize,
  };

  const { data: versionFilesResponse } = useGetVersionFilesQuery(queryParams);

  useEffect(() => {
    if (versionFilesResponse) {
      dispatch(
        setLatestVersion(
          versionFilesResponse?.versions?.[0]?.version.toString()
        )
      );
      // Set default version name to "Version X" where X is nextVersionNumber
      setVersionName(`Version ${nextVersionNumber}`);
    }
  }, [versionFilesResponse, nextVersionNumber, dispatch]);

  const [createVersion] = useCreateNewVersionMutation();
  const [confirmPublish] = useConfirmPublishMutation();

  const handleSubmit = async () => {
    let hasError = false;

    if (!versionName.trim()) {
      setVersionNameError('Version name is required.');
      hasError = true;
    } else {
      setVersionNameError(null);
    }

    if (!releaseNote.trim()) {
      setReleaseNoteError('Release note is required.');
      hasError = true;
    } else {
      setReleaseNoteError(null);
    }

    if (hasError) {
      return;
    }

    setIsSubmitting(true);
    setVersionNameError(null);
    setPublishProgressMessage('Publishing...');

    try {
      const payload = {
        notes: releaseNote,
        name: versionName,
      };
      await createVersion(payload).unwrap();

      setPublishProgressMessage('Finalizing publish...');
      const confirmationResponse = await waitForPublishConfirmation({
        confirmPublish: () => confirmPublish().unwrap(),
      });

      if (!confirmationResponse.success) {
        throw new Error('Something went wrong');
      }

      enqueueSnackbar('Version published successfully', { variant: 'success' });
      onPublishSuccess();
      onClose();
    } catch (err: unknown) {
      const error = err as { data?: { message?: string }; message?: string };
      setVersionNameError(
        error?.data?.message ||
          error?.message ||
          'Failed to publish version. Please try again.'
      );
      enqueueSnackbar('Failed to publish version.', { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setVersionName(`Version ${nextVersionNumber}`);
    setReleaseNote('');
    setVersionNameError(null);
    setReleaseNoteError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-gray-100 rounded-lg shadow-xl w-full max-w-md m-4">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-800">
            Publish Version
          </h2>
          <button
            onClick={handleCancel}
            className="text-gray-500 hover:text-gray-700 focus:outline-none"
            aria-label="Close modal"
            disabled={isSubmitting}
          >
            <KeenIcon icon="cross" className="text-lg" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <span className="block text-sm font-medium text-gray-700 mb-1">
              Next Publish Version
            </span>
            <input
              type="text"
              value={`Version ${nextVersionNumber}`}
              disabled
              className="w-full p-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-700"
              aria-label="Next publish version"
            />
          </div>
          <div>
            <span className="block text-sm font-medium text-gray-700 mb-1">
              Version Name *
            </span>
            <input
              type="text"
              value={versionName}
              onChange={(e) => {
                setVersionName(e.target.value);
                if (e.target.value.trim()) {
                  setVersionNameError(null);
                }
              }}
              className={`input w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                versionNameError ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Enter version name"
              aria-label="Version name"
              aria-required="true"
              autoComplete="off"
            />
            {versionNameError && (
              <div className="mt-1 text-red-600 text-sm flex items-center gap-2">
                <span>{versionNameError}</span>
              </div>
            )}
          </div>
          <div>
            <span className="block text-sm font-medium text-gray-700 mb-1">
              Release Note *
            </span>
            <textarea
              value={releaseNote}
              onChange={(e) => {
                setReleaseNote(e.target.value);
                if (e.target.value.trim()) {
                  setReleaseNoteError(null);
                }
              }}
              className={`input w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 h-24 resize-none ${
                releaseNoteError ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Enter release notes"
              aria-label="Release note"
              aria-required="true"
            />
            {releaseNoteError && (
              <div className="mt-1 text-red-600 text-sm flex items-center gap-2">
                <span>{releaseNoteError}</span>
              </div>
            )}
          </div>
          {isSubmitting && (
            <div className="text-gray-600 text-sm flex items-center gap-2">
              <KeenIcon icon="spinner" className="animate-spin text-lg" />
              <span>{publishProgressMessage}</span>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 p-4 border-t border-gray-200">
          <button
            onClick={handleCancel}
            className="btn btn-sm btn-light"
            aria-label="Cancel publish"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="btn btn-sm btn-primary"
            disabled={isSubmitting}
            aria-label="Publish version"
          >
            Publish
          </button>
        </div>
      </div>
    </div>
  );
};

export default PublishVersionModal;
